import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import {
  ProgressButtonComponent,
  ProgressButtonModel,
} from "@syncfusion/ej2-react-splitbuttons";
import * as React from "react";
import { useEffect } from "react";

export interface LoadingButtonProps
  extends Omit<
    ProgressButtonModel,
    "content" | "isPrimary" | "onClick" | "iconCss"
  > {
  text?: string;
  minimal?: boolean;
  isLoading?: boolean;
  loadingProgressContent?: string;
  loadingSuccessContent?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  iconProps?: FontAwesomeIconProps;
  buttonClassStyle?: string;
  defaultLoadingIdleContent?: string;
  defaultLoadingProgressContent?: string;
  defaultLoadingSuccessContent?: string;
}

export interface LoadingState {
  content: string;
  cssClass: string;
}

const useLoadingButton = (
  props: LoadingButtonProps,
  buttonClassStyle: string,
  defaultLoadingIdleContent: string,
  defaultLoadingProgressContent: string,
  defaultLoadingSuccessContent: string
) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [loadingState, setLoadingState] = React.useState<LoadingState>();

  const progressButtonComponent = React.useRef<ProgressButtonComponent>(null);

  const duration = props.duration ?? 81_000_000;

  useEffect(() => {
    setLoadingState({
      ...{
        content: props.text ?? defaultLoadingIdleContent,
        cssClass: `${buttonClassStyle} ${props.cssClass}`,
      },
    });
  }, [props.text, defaultLoadingIdleContent, buttonClassStyle, props.cssClass]);

  useEffect(() => {
    if (props.isLoading) {
      progressButtonComponent.current?.start();

      setLoadingState({
        ...{
          content:
            props.loadingProgressContent ?? defaultLoadingProgressContent,
          cssClass: `${buttonClassStyle} ${props.cssClass}`,
        },
      });
    } else {
      if (isLoading) {
        setLoadingState({
          ...{
            content:
              props.loadingSuccessContent ?? defaultLoadingSuccessContent,
            cssClass: `${buttonClassStyle} ${props.cssClass} e-success`,
          },
        });
        setTimeout(() => {
          progressButtonComponent.current?.progressComplete();
          setDefaultLoadingState();
        }, 500);
      }
    }

    setIsLoading(props.isLoading ?? false);
  }, [props.isLoading]);

  const setDefaultLoadingState = () => {
    setLoadingState({
      ...{
        content: props.text ?? defaultLoadingIdleContent,
        cssClass: `${buttonClassStyle} ${props.cssClass}`,
      },
    });
  };

  const onClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.onClick) {
      props.onClick(e);
      progressButtonComponent.current?.progressComplete();
    }
  };

  return {
    loadingState,
    isLoading,
    progressButtonComponent,
    onClickHandler,
    duration,
  };
};

export default useLoadingButton;
